// ═══════════════════════════════════════════════════════════════════
// CASOS — Librería core del módulo Casos
// ═══════════════════════════════════════════════════════════════════
//
// Entidad transversal: expedientes investigativos de días/semanas con
// conversación, participantes internos+externos, vinculados a recursos
// y tareas. Origen polimórfico (webhooks ML, manual, regla, etc.).
//
// Ver:
//   - docs/casos.md            — spec completa del módulo
//   - docs/AUDITORIA_PLATAFORMA.md  — convenciones del proyecto
//   - src/core/lib/tiposCaso.js     — catálogo + state machine
//
// API exportada:
//
//   CRUD / Identidad:
//     crearCaso(empresaId, data)          → casoId
//     getCaso(casoId)                     → doc completo o null
//     actualizarCaso(casoId, data)        → update parcial
//
//   State machine:
//     cambiarEstado(casoId, estado, motivo, autor)
//     transferirCaso(casoId, nuevoAsignadoA, motivo, autor)
//     escalarCaso(casoId, supervisorId, razon, autor)
//     cerrarCaso(casoId, conclusion, motivoCierre, autor, opts)
//     reabrirCaso(casoId, motivo, autor)
//
//   Case Team:
//     agregarParticipante(casoId, participante)
//     removerParticipante(casoId, userId)
//     cambiarOwner(casoId, nuevoOwnerId, autor)
//
//   Case Feed (append-only):
//     agregarComentario(casoId, contenido, visibilidad, autor)
//     agregarEventoFeed(casoId, empresaId, evento)     — helper interno
//
//   Adjuntos / Aprobaciones:
//     agregarAdjunto(casoId, adjunto, autor)
//     solicitarAprobacion(casoId, aprobadorId, motivo, autor)
//
//   Vínculos:
//     vincularTareaHija(casoId, tareaId)
//     desvincularTareaHija(casoId, tareaId)
//     vincularRecurso(casoId, recurso)
//
//   Numeración (B1.3):
//     generarNumeroCaso(empresaId)        — secuencial CASE-YYYY-NNNNN
//
// ═══════════════════════════════════════════════════════════════════

import {
  collection, doc, getDoc, setDoc, updateDoc, addDoc, getDocs, query, where,
  serverTimestamp, runTransaction, arrayUnion, arrayRemove,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forCreate, forUpdate, sanitize, requireString } from './dbHelpers'
import {
  getTipoCaso, getTipoCasoPorCodigo, validarTransicion, resolverSLAsCaso,
  CASO_ESTADOS,
} from './tiposCaso'
import { iniciarSLA, resolverSLA as resolverSLAInstancia, instanciasVivasDeRecurso } from './slas'
import { notificar, notificarVarios, NOTIFICACION_TIPOS, NOTIFICACION_PRIORIDADES } from './notificaciones'
import { aplicarReglasAsignacion } from './reglasCaso'

// ──────── Constantes ────────

export const CASO_PRIORIDADES = ['baja', 'normal', 'alta', 'critica']
export const CASO_SEVERIDADES = ['s1', 's2', 's3', 's4']  // s1 = más severo

// Los permisos del módulo Casos NO viven acá. Viven —como los de todos los
// módulos— en `MODULOS_CATALOGO` (`src/core/lib/modulos.js`), que es la fuente
// única que `constraints.md §1.B` declara y que alimenta la matriz de
// `/permisos`.
//
// Acá había una lista `CASOS_PERMISOS` de 13 códigos, muerta desde que se
// retiró el sistema de Vistas (`src/core/hooks/usePermisos.js:20`) que era su
// único consumidor: cero importadores en todo el repo. Había driftado contra el
// catálogo —sólo 7 de 13 códigos en común— y la documentación la servía como
// viva, así que el próximo permiso de Casos nacía en la lista equivocada y
// quedaba inasignable desde `/permisos`. Es la misma clase de defecto que el
// ítem 6h cerró en `reportes.editar`: un gate que no se puede tildar.

export const FEED_TIPOS = {
  COMENTARIO:           'comentario',
  MENSAJE_ENTRANTE:     'mensaje-entrante',
  MENSAJE_SALIENTE:     'mensaje-saliente',
  CAMBIO_ESTADO:        'cambio-estado',
  CAMBIO_ASIGNACION:    'cambio-asignacion',
  CAMBIO_OWNER:         'cambio-owner',
  // #33 — Flow de aceptación: cuando un caso nace asignado a alguien != owner,
  // arranca en 'nuevo'. El asignado debe aceptar o rechazar.
  CASO_ACEPTADO:        'caso-aceptado',
  CASO_RECHAZADO:       'caso-rechazado',
  PARTICIPANTE_AGREGADO:'participante-agregado',
  PARTICIPANTE_REMOVIDO:'participante-removido',
  SLA_BREACH:           'sla-breach',
  SLA_AT_RISK:          'sla-at-risk',
  TAREA_CREADA:         'tarea-creada',
  TAREA_CERRADA:        'tarea-cerrada',
  EVENTO_EXTERNO:       'evento-externo',
  SUGERENCIA_KB:        'sugerencia-kb',
  APROBACION_SOLICITADA:'aprobacion-solicitada',
  APROBACION_RESUELTA:  'aprobacion-resuelta',
  PROPUESTA_AGENTE:     'propuesta-agente',
  ACCION_AGENTE:        'accion-agente',
  CIERRE:               'cierre',
  REAPERTURA:           'reapertura',
}

export const FEED_VISIBILIDAD = {
  INTERNO: 'interno',   // solo users del sistema
  PUBLICO: 'publico',   // visible al participante externo en portal
}

// ──────── Refs (helpers) ────────

const casoRef = (casoId) => doc(db, COLLECTIONS.CASOS, casoId)
const feedColRef = (casoId) => collection(db, COLLECTIONS.CASOS, casoId, COLLECTIONS.CASOS_FEED)
const adjuntosColRef = (casoId) => collection(db, COLLECTIONS.CASOS, casoId, COLLECTIONS.CASOS_ADJUNTOS)
const aprobacionesColRef = (casoId) => collection(db, COLLECTIONS.CASOS, casoId, COLLECTIONS.CASOS_APROBACIONES)

// ──────── Notificaciones helpers (B4.2) ────────

/**
 * Devuelve userIds únicos del case team interno excluyendo `excluirUid` opcional.
 * Útil para "notificar a todo el team excepto al que hizo la acción".
 */
function caseTeamUserIds(caso, excluirUid = null) {
  const ids = new Set()
  if (caso.owner) ids.add(caso.owner)
  if (caso.asignadoA) ids.add(caso.asignadoA)
  for (const uid of (caso.participantesUserIds || [])) ids.add(uid)
  if (excluirUid) ids.delete(excluirUid)
  return Array.from(ids)
}

/** Quitar campos undefined de un objeto plano (Firestore no acepta undefined). */
function sanitizarUndefined(obj) {
  if (!obj || typeof obj !== 'object') return obj
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue
    if (v && typeof v === 'object' && !Array.isArray(v) && !v.toMillis) {
      out[k] = sanitizarUndefined(v)
    } else {
      out[k] = v
    }
  }
  return out
}

/** Notificación silenciosa (no rompe si falla).
 *
 * #33 — sanitizamos `metadatos` antes de mandar a Firestore. Si quien llama
 * pasa { tipo: caso.tipo } y caso.tipo es undefined, Firestore rechaza el
 * addDoc. La sanitización lo deja como vacío en vez de fallar. Visible en
 * la consola anteriormente como: "addDoc() called with invalid data.
 * Unsupported field value: undefined (found in field metadatos.tipo)".
 */
async function notificarSeguro(args) {
  try {
    const safe = { ...args }
    if (safe.metadatos) safe.metadatos = sanitizarUndefined(safe.metadatos)
    return await notificar(safe)
  } catch (e) { console.warn('[casos.notif]', e?.message); return null }
}

/** Notificación batch silenciosa. */
async function notificarVariosSeguro(uids, datos) {
  try {
    const safe = { ...datos }
    if (safe.metadatos) safe.metadatos = sanitizarUndefined(safe.metadatos)
    return await notificarVarios(uids, safe)
  } catch (e) { console.warn('[casos.notif]', e?.message); return null }
}

// ──────── Numeración secuencial (B1.3) ────────

/**
 * Genera un número de caso único y consecutivo por empresa+año.
 * Formato: CASE-YYYY-NNNNN (ej: CASE-2026-00042).
 *
 * Usa Firestore transaction para garantizar unicidad bajo concurrencia.
 * Contador vive en counters/CASE-{year}-{empresaId}.
 *
 * @param {string} empresaId
 * @returns {Promise<string>} numero de caso
 */
export async function generarNumeroCaso(empresaId) {
  if (!empresaId) throw new Error('[casos] empresaId requerido para generar número')
  const year = new Date().getUTCFullYear()
  const counterId = `CASE-${year}-${empresaId}`
  const counterRef = doc(db, 'counters', counterId)

  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef)
    const lastNumber = snap.exists() ? (snap.data().lastNumber || 0) : 0
    const newNumber = lastNumber + 1
    tx.set(counterRef, {
      lastNumber: newNumber,
      year,
      empresaId,
      actualizadoEn: serverTimestamp(),
    }, { merge: true })
    return `CASE-${year}-${String(newNumber).padStart(5, '0')}`
  })
}

// ──────── Participantes (helpers internos) ────────

/**
 * Calcula el array derivado participantesUserIds[] a partir de participantes[].
 * Necesario para que las Firestore rules puedan validar acceso con operador `in`.
 * Solo incluye participantes de tipo user-interno (no compradores externos, etc.).
 *
 * @param {Array} participantes
 * @returns {string[]}
 */
function calcularParticipantesUserIds(participantes) {
  if (!Array.isArray(participantes)) return []
  return participantes
    .filter(p => p && p.tipo === 'user-interno' && p.userId)
    .map(p => p.userId)
}

// ──────── Case Feed (append-only) ────────

/**
 * El `empresaId` de un caso, leído del DOC del caso (report
 * nZoYmy6lgsMN28vDBwBU). Es la única fuente: las rules de las subcolecciones
 * deciden con el del caso padre, y el staff opera casos de otras empresas, así
 * que el de la sesión sería el equivocado. Sin caso, o con un caso sin
 * `empresaId`, falla antes de escribir (constraints §1.E).
 */
async function empresaIdDelCaso(casoId) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)
  return requireString(caso.empresaId, 'caso.empresaId')
}

/**
 * Escribe un evento al case feed.
 *
 * @param {string} casoId
 * @param {Object} evento  — { tipo (FEED_TIPOS), autor, contenido, visibilidad, canal, ...extras }
 * @returns {Promise<string>} eventoId
 */
export async function agregarEventoFeed(casoId, empresaId, evento) {
  const visibilidad = evento.visibilidad === FEED_VISIBILIDAD.PUBLICO
    ? FEED_VISIBILIDAD.PUBLICO : FEED_VISIBILIDAD.INTERNO

  const payload = {
    tipo: requireString(evento.tipo, 'feed.tipo'),
    autor: evento.autor || null,         // userId, contacto externo, o 'sistema' o 'agente:{agenteId}'
    autorTipo: evento.autorTipo || 'sistema',  // 'user-interno' | 'externo' | 'agente' | 'sistema'
    contenido: evento.contenido ?? null,
    visibilidad,
    canal: evento.canal || null,          // 'in-app' | 'email' | 'sms' | 'wa' | 'portal' | 'ml'
    metadata: evento.metadata || null,
    empresaId: requireString(empresaId, 'feed.empresaId'),
    creadoEn: serverTimestamp(),
  }

  const ref = await addDoc(feedColRef(casoId), payload)
  return ref.id
}

/**
 * Atajo para agregar un comentario (caso de uso más común del feed).
 *
 * @param {string} casoId
 * @param {string} contenido
 * @param {string} visibilidad   — FEED_VISIBILIDAD.INTERNO | PUBLICO (default INTERNO)
 * @param {string} autor         — userId
 * @returns {Promise<string>} eventoId
 */
export async function agregarComentario(casoId, contenido, visibilidad = FEED_VISIBILIDAD.INTERNO, autor = null) {
  return agregarEventoFeed(casoId, await empresaIdDelCaso(casoId), {
    tipo: FEED_TIPOS.COMENTARIO,
    autor,
    autorTipo: 'user-interno',
    contenido: requireString(contenido, 'contenido'),
    visibilidad,
    canal: 'in-app',
  })
}

// ──────── CRUD / Identidad ────────

/**
 * Crea un caso nuevo.
 *
 * @param {string} empresaId
 * @param {Object} data — payload del caso
 * @returns {Promise<{ id, numero }>}
 */
export async function crearCaso(empresaId, data) {
  if (!empresaId) throw new Error('[casos] empresaId requerido')

  // Resolver tipoCaso (acepta tipoCasoId, tipoCasoCodigo, o tipo directo)
  let tipoCaso = null
  if (data.tipoCasoId) {
    tipoCaso = await getTipoCaso(data.tipoCasoId)
  } else if (data.tipo) {
    tipoCaso = await getTipoCasoPorCodigo(data.tipo)
  }
  if (!tipoCaso) {
    throw new Error('[casos] tipoCaso no resuelto — requiere tipoCasoId o tipo (código)')
  }

  // Estado inicial
  // B8.3 — Si el asignado es distinto del owner/creador, el caso nace en
  // estado "nuevo" (propuesto, esperando aceptación). Si es el mismo user,
  // arranca directamente en "abierto" (tipoCaso.estadoInicial).
  // Mismo patrón que tareas.js#crearAccionable con requiereAceptacion.
  // Esta lógica se resuelve más abajo cuando ya tenemos asignadoA final.
  let estado = data.estado || tipoCaso.estadoInicial || CASO_ESTADOS.ABIERTO

  // Numeración secuencial
  const numero = await generarNumeroCaso(empresaId)

  // Owner / asignado defaults
  const owner = data.owner || data.creadoPor || null
  let asignadoA = data.asignadoA || owner

  // ─── B6.1 — Aplicar reglas de asignación automática ───
  // Si el caller no especificó `asignadoA` explícito (o vino igual al owner),
  // evaluamos reglas declarativas que pueden re-asignar según tipo/cat/prioridad.
  let reglaAsignacionAplicada = null
  if (!data.asignadoA || data.asignadoA === owner) {
    const resultado = await aplicarReglasAsignacion({
      tipoCasoCodigo: tipoCaso.codigo,
      categoria: data.categoria || null,
      area: tipoCaso.area || data.area || null,
      prioridad: data.prioridad || tipoCaso.prioridadDefault || 'normal',
    })
    if (resultado?.asignadoAUserId) {
      asignadoA = resultado.asignadoAUserId
      reglaAsignacionAplicada = resultado.reglaAplicada
    }
  }

  // Participantes iniciales (case team interno)
  const participantesIniciales = []
  if (owner) {
    participantesIniciales.push({ tipo: 'user-interno', userId: owner, rol: 'owner', agregadoEn: new Date().toISOString() })
  }
  if (asignadoA && asignadoA !== owner) {
    participantesIniciales.push({ tipo: 'user-interno', userId: asignadoA, rol: 'asignado', agregadoEn: new Date().toISOString() })
  }
  // Sumar participantes externos opcionales (comprador, proveedor)
  if (Array.isArray(data.participantes)) {
    for (const p of data.participantes) {
      if (p?.tipo && p?.tipo !== 'user-interno') participantesIniciales.push({ ...p, agregadoEn: new Date().toISOString() })
    }
  }
  const participantesUserIds = calcularParticipantesUserIds(participantesIniciales)

  // Multi-SLA defaults del tipo — los slaInstancias reales se crean DESPUÉS
  // del setDoc del caso (necesitan el casoId). Acá solo precalculamos deadlines
  // para visualización inmediata; el motor SLA real toma los datos de slaInstancias.
  const slasDefaults = resolverSLAsCaso(tipoCaso)
  const ahora = Date.now()
  const slas = slasDefaults.map((s) => ({
    // slaInstanciaId se completa después del setDoc (ver bloque post-create)
    slaInstanciaId: null,
    slaCodigo: `caso-${tipoCaso.codigo}-${s.tipo}`,  // ref al slaDoc canónico
    tipo: s.tipo,
    contrareloj: ahora,
    deadline: ahora + s.minutos * 60 * 1000,
    ventanaAlertaMin: s.ventanaAlertaMin,
    estado: 'on-track',
    cumplidoEn: null,
  }))

  // Payload final
  // Fallback de `tipo`: si el tipoCaso no tiene `codigo` seteado (caso de docs
  // creados desde una UI legacy sin el campo), usamos el id como fallback para
  // no perder la categorización. Esto evita que la columna TIPO de la lista
  // muestre "—" y rompe filtros/métricas por tipo.
  const tipoFinal = tipoCaso.codigo || tipoCaso.id
  const casoData = sanitize({
    numero,
    tipo: tipoFinal,
    tipoCasoId: tipoCaso.id,
    titulo: data.titulo || null,
    descripcion: data.descripcion || null,

    // Clasificación
    categoria: data.categoria || null,
    subcategoria: data.subcategoria || null,
    tags: Array.isArray(data.tags) ? data.tags : [],

    // Severidad / prioridad
    prioridad: data.prioridad || tipoCaso.prioridadDefault || 'normal',
    severidad: data.severidad || tipoCaso.severidadDefault || 's3',

    // Estado
    estado,
    motivoCierre: null,

    // Case team
    owner,
    asignadoA,
    participantes: participantesIniciales,
    participantesUserIds,  // derivado para rules

    // Configuration Items
    recursosVinculados: Array.isArray(data.recursosVinculados) ? data.recursosVinculados : [],

    // Hierarchy
    casoPadre: data.casoPadre || null,
    casosHijos: [],
    casosRelacionados: [],

    // Multi-SLA
    slas,

    // Tareas hijas
    tareasHijas: [],

    // Origen
    origen: data.origen || { canal: 'manual', modulo: null, triggerId: null },

    // Entitlement (opcional)
    entitlementId: data.entitlementId || null,

    // KCS / Knowledge
    aprendizajeId: null,
    articulosSugeridos: [],

    // CSAT (al cierre)
    csat: null,

    // Tracking temporal
    creadoPor: owner,
    primeraRespuestaEn: null,
    cerradoEn: null,
    cerradoPor: null,
    reabiertoCount: 0,
    ultimaActividadEn: serverTimestamp(),

    // Confidencialidad
    visibilidad: data.visibilidad || 'normal',
    pii: !!data.pii,

    // Dedupe (cuando viene de webhook automático)
    dedupeKey: data.dedupeKey || null,

    // Versionado
    schemaVersion: 1,
    activo: true,
  })

  // Generar ID y escribir (usamos addDoc para auto-id, luego pisamos con setDoc para retornar ID arriba)
  const ref = doc(collection(db, COLLECTIONS.CASOS))
  await setDoc(ref, forCreate(casoData, empresaId))

  // ─── B4.1 — Crear slaInstancias en el motor existente ───
  // Cada SLA declarado en el tipoCaso → una instancia viva en /slaInstancias
  // que el motor `evaluarSLAsCore` (functions/slas.js) procesa cada 5 min.
  // El motor envía notificaciones por vencer/vencido al responsable y supervisores.
  if (slas.length > 0) {
    try {
      // Resolver los slaIds del catálogo /slas por código (sembrados por
      // seed-tipos-caso.js → seedSLAsDeCasos).
      const codigos = slas.map(s => s.slaCodigo)
      const slasSnap = await getDocs(query(
        collection(db, COLLECTIONS.SLAS),
        where('codigo', 'in', codigos.slice(0, 10)),  // Firestore in clause max 10
      ))
      const slasPorCodigo = new Map()
      slasSnap.docs.forEach(d => slasPorCodigo.set(d.data().codigo, d.id))

      // Iniciar cada SLA. Guardar el slaInstanciaId en el doc del caso.
      for (let i = 0; i < slas.length; i++) {
        const sla = slas[i]
        const slaId = slasPorCodigo.get(sla.slaCodigo)
        if (!slaId) {
          console.warn(`[crearCaso] SLA catálogo no encontrado: ${sla.slaCodigo}. Ejecutá __seedTiposCaso() desde consola admin.`)
          continue
        }
        const inst = await iniciarSLA({
          slaId,
          recursoTipo: 'caso',
          recursoId: ref.id,
          responsableUserId: asignadoA,
          empresaId,
        })
        slas[i].slaInstanciaId = inst.id
      }

      // Update el doc del caso con los slaInstanciaIds resueltos
      await updateDoc(ref, forUpdate({ slas }))
    } catch (e) {
      console.warn('[crearCaso] iniciar SLAs falló (no-fatal):', e.message)
      // Los slas[] quedan en el doc del caso sin slaInstanciaId pero
      // con la info para visualización inmediata. El motor no los va a
      // procesar hasta que se re-iniciaaen, pero el caso queda funcional.
    }
  }

  // Evento inicial al feed
  await agregarEventoFeed(ref.id, empresaId, {
    tipo: FEED_TIPOS.CAMBIO_ESTADO,
    autor: owner || 'sistema',
    autorTipo: owner ? 'user-interno' : 'sistema',
    contenido: `Caso creado en estado "${estado}"`,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { estadoNuevo: estado },
  })

  // B6.1 — Loguear regla de asignación aplicada (si hubo) al feed
  if (reglaAsignacionAplicada) {
    await agregarEventoFeed(ref.id, empresaId, {
      tipo: FEED_TIPOS.CAMBIO_ASIGNACION,
      autor: 'sistema',
      autorTipo: 'sistema',
      contenido: `Asignación automática: regla "${reglaAsignacionAplicada.nombre || reglaAsignacionAplicada.codigo}"`,
      visibilidad: FEED_VISIBILIDAD.INTERNO,
      metadata: { reglaAplicada: reglaAsignacionAplicada, asignadoNuevo: asignadoA },
    })
  }

  // ─── B4.2 — Notificar al asignado si no es el creador ───
  if (asignadoA && asignadoA !== owner) {
    await notificarSeguro({
      userId: asignadoA,
      tipo: NOTIFICACION_TIPOS.CASO_ASIGNADO,
      titulo: `Te asignaron el caso ${numero}`,
      cuerpo: casoData.titulo || '',
      link: `/casos/${ref.id}`,
      recursoTipo: 'caso',
      recursoId: ref.id,
      empresaId,
      prioridad: casoData.prioridad === 'critica'
        ? NOTIFICACION_PRIORIDADES.CRITICA
        : casoData.prioridad === 'alta'
        ? NOTIFICACION_PRIORIDADES.ALTA
        : NOTIFICACION_PRIORIDADES.NORMAL,
      metadatos: { tipo: casoData.tipo, prioridad: casoData.prioridad },
    })
  }

  // Auto-response si el tipo lo declara
  if (tipoCaso.autoResponse?.activo) {
    await agregarEventoFeed(ref.id, empresaId, {
      tipo: FEED_TIPOS.COMENTARIO,
      autor: 'sistema',
      autorTipo: 'sistema',
      contenido: tipoCaso.autoResponse.contenido || 'Hola, recibimos tu caso. Te respondemos a la brevedad.',
      visibilidad: FEED_VISIBILIDAD.PUBLICO,
      canal: 'auto-response',
    })
  }

  return { id: ref.id, numero }
}

/**
 * Obtiene un caso por ID.
 *
 * @param {string} casoId
 * @returns {Promise<Object|null>}
 */
export async function getCaso(casoId) {
  const snap = await getDoc(casoRef(casoId))
  return snap.exists() ? docConId(snap) : null
}

/**
 * Actualiza campos arbitrarios de un caso. Sanitiza el payload.
 * Para cambios de estado, transferencias, escalamientos: usar las
 * funciones específicas que escriben evento al feed.
 *
 * @param {string} casoId
 * @param {Object} data
 */
export async function actualizarCaso(casoId, data) {
  // No permitir cambiar empresaId (anti "robo de caso")
  // No permitir cambiar numero (es inmutable)
  const { empresaId: _eId, numero: _n, ...safe } = data
  await updateDoc(casoRef(casoId), forUpdate(sanitize(safe)))
}

// ──────── State Machine ────────

// Estado terminal → el wrapper que hay que usar para llegar a él. La tabla
// existe para que el mensaje del guard nombre la salida: un error que sólo
// dice "no se puede" manda a leer el código.
const WRAPPER_DE_ESTADO = {
  [CASO_ESTADOS.REABIERTO]:           'reabrirCaso()',
  [CASO_ESTADOS.CERRADO]:             'cerrarCaso()',
  [CASO_ESTADOS.CERRADO_SIN_ACCION]:  'cerrarCaso() con opts.estadoFinal',
  [CASO_ESTADOS.ESCALADO]:            'escalarCaso()',
}

/**
 * Cambia el estado de un caso validando contra las transiciones permitidas
 * por su tipoCaso. Escribe evento al feed.
 *
 * ⚠️ LOS CUATRO ESTADOS CON WRAPPER NO ENTRAN POR ACÁ. Cada uno tiene efectos
 * propios que esta función no hace, y llegar al estado sin ellos es una
 * transición a medias, silenciosa:
 *
 *   · `reabierto`           → `reabrirCaso()`  — suma a `reabiertoCount`, limpia
 *     el cierre, escribe REAPERTURA en el feed, avisa al case team.
 *   · `cerrado`             → `cerrarCaso()`   — escribe `motivoCierre`,
 *     `cerradoEn`, `cerradoPor`, el evento CIERRE, y resuelve las
 *     `slaInstancias` vivas (sin eso siguen corriendo contra un caso cerrado y
 *     marcan breach).
 *   · `cerrado-sin-accion`  → `cerrarCaso()` con `opts.estadoFinal` — es el
 *     MISMO cierre por la puerta de al lado: candadear sólo `cerrado` dejaba
 *     intacto el mismo agujero.
 *   · `escalado`            → `escalarCaso()` — transfiere al supervisor (que
 *     lo suma al case team) y lo notifica con prioridad alta.
 *
 * El guard está acá —y no en cada llamador— porque los llamadores son tres
 * (el kanban, el dropdown del detalle y las macros) más `window.__casos`, y ya
 * se demostró que olvidar uno es lo que pasa (report TotR4mis7Xho06o3HZ7X: el
 * kanban y las macros escribían `reabierto` por el costado; report
 * kWnFkJXHl9GvUJmNTwwH: las tres puertas escribían `cerrado` y `escalado`
 * igual). Ésta es la explicación canónica: los call sites la citan, no la
 * repiten.
 *
 * ⚠️ Es un guard DE CLIENTE, no una frontera de autorización: ninguna de las
 * escrituras de este archivo tiene callable ni rule que la replique del lado
 * servidor (deuda declarada, report gMWffCliBHz5BsqslDcT). Alcanza para lo que
 * resuelve —integridad del dato que escribe la UI— y no se cita como candado
 * de seguridad.
 *
 * @param {string} casoId
 * @param {string} estadoNuevo
 * @param {string} motivo        — texto descriptivo del cambio
 * @param {string} autor         — userId
 * @throws si `estadoNuevo` tiene wrapper — usar el que el mensaje nombra
 */
export async function cambiarEstado(casoId, estadoNuevo, motivo = null, autor = null) {
  const wrapper = WRAPPER_DE_ESTADO[estadoNuevo]
  if (wrapper) {
    throw new Error(`[casos] pasar un caso a "${estadoNuevo}" va por ${wrapper}, no por cambiarEstado()`)
  }
  return aplicarTransicion(casoId, estadoNuevo, motivo, autor)
}

/**
 * Carga el caso y exige que `estado actual → estadoNuevo` esté permitido por
 * su tipoCaso (la matriz es config por tenant). Sin escribir nada: es la
 * validación de `aplicarTransicion` sola, para que un wrapper que hace otros
 * efectos ANTES de la transición (`escalarCaso` transfiere) pueda fallar
 * antes de ellos y no dejar el caso a medias (report rX7S5oKYTeQaR5ysjXZe).
 *
 * @param {string} casoId
 * @param {string} estadoNuevo
 * @returns {Promise<Object>} el caso, tal como estaba al validar
 * @throws si el caso o su tipoCaso no existen, o la transición no está permitida
 */
async function cargarCasoConTransicionValida(casoId, estadoNuevo) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)

  const tipoCaso = await getTipoCaso(caso.tipoCasoId)
  if (!tipoCaso) throw new Error(`[casos] tipoCaso ${caso.tipoCasoId} no encontrado`)

  if (!validarTransicion(tipoCaso, caso.estado, estadoNuevo)) {
    throw new Error(`[casos] transición no permitida: ${caso.estado} → ${estadoNuevo}`)
  }
  return caso
}

/**
 * El cuerpo de la transición, sin el guard de los estados con wrapper.
 * Interno: los únicos llamadores legítimos además de `cambiarEstado` son los
 * wrappers mismos (`reabrirCaso`, `cerrarCaso`, `escalarCaso`), que ya hacen
 * los efectos que el guard protege — si pasaran por `cambiarEstado` se
 * candadearían a sí mismos.
 *
 * @param {string} casoId
 * @param {string} estadoNuevo
 * @param {string} motivo
 * @param {string} autor
 */
async function aplicarTransicion(casoId, estadoNuevo, motivo = null, autor = null) {
  const caso = await cargarCasoConTransicionValida(casoId, estadoNuevo)

  await updateDoc(casoRef(casoId), forUpdate({
    estado: estadoNuevo,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.CAMBIO_ESTADO,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: motivo,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { estadoAnterior: caso.estado, estadoNuevo },
  })
}

/**
 * Transfiere el caso a un nuevo asignado. El owner NO cambia.
 *
 * @param {string} casoId
 * @param {string} nuevoAsignadoA
 * @param {string} motivo
 * @param {string} autor
 */
export async function transferirCaso(casoId, nuevoAsignadoA, motivo = null, autor = null) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)

  // Sumar al case team si no estaba ya
  let participantes = caso.participantes || []
  if (nuevoAsignadoA && !participantes.find(p => p.userId === nuevoAsignadoA && p.tipo === 'user-interno')) {
    participantes = [...participantes, {
      tipo: 'user-interno', userId: nuevoAsignadoA, rol: 'asignado', agregadoEn: new Date().toISOString(),
    }]
  }
  const participantesUserIds = calcularParticipantesUserIds(participantes)

  await updateDoc(casoRef(casoId), forUpdate({
    asignadoA: nuevoAsignadoA,
    participantes,
    participantesUserIds,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.CAMBIO_ASIGNACION,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: motivo,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { asignadoAnterior: caso.asignadoA, asignadoNuevo: nuevoAsignadoA },
  })

  // ─── B4.2 — Notificar al nuevo asignado ───
  if (nuevoAsignadoA && nuevoAsignadoA !== autor) {
    await notificarSeguro({
      userId: nuevoAsignadoA,
      tipo: NOTIFICACION_TIPOS.CASO_TRANSFERIDO,
      titulo: `Te transfirieron el caso ${caso.numero || casoId.slice(0, 8)}`,
      cuerpo: motivo || caso.titulo || '',
      link: `/casos/${casoId}`,
      recursoTipo: 'caso',
      recursoId: casoId,
      empresaId: caso.empresaId,
      prioridad: NOTIFICACION_PRIORIDADES.NORMAL,
      metadatos: { tipo: caso.tipo, asignadoAnterior: caso.asignadoA },
    })
  }
}

/**
 * #33 — Aceptar un caso recién recibido.
 *
 * Cuando un caso nace asignado a alguien != owner (origen humano o vía regla),
 * arranca en estado 'nuevo'. El asignado tiene que aceptarlo (lo toma) o
 * rechazarlo (vuelve a sin asignar para que otro lo tome).
 *
 * Aceptar = cambiar estado 'nuevo' → 'abierto'. Mantiene asignadoA.
 *
 * @param {string} casoId
 * @param {string} autor — userId del que acepta. Debe coincidir con asignadoA.
 */
export async function aceptarCaso(casoId, autor = null) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)
  if (caso.estado !== CASO_ESTADOS.NUEVO) {
    throw new Error(`[casos] solo se puede aceptar un caso en estado 'nuevo' (actual: ${caso.estado})`)
  }
  if (!caso.asignadoA) {
    throw new Error(`[casos] el caso no tiene asignado — no hay nada que aceptar`)
  }

  await updateDoc(casoRef(casoId), forUpdate({
    estado: CASO_ESTADOS.ABIERTO,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.CASO_ACEPTADO,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: null,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { asignadoA: caso.asignadoA, estadoAnterior: caso.estado, estadoNuevo: CASO_ESTADOS.ABIERTO },
  })
}

/**
 * #33 — Rechazar un caso recién recibido.
 *
 * El asignado devuelve el caso sin tomarlo. Vuelve a estado 'nuevo' SIN asignado
 * para que el owner u otro pueda re-asignarlo. Motivo obligatorio para que el
 * próximo asignado entienda el contexto.
 *
 * @param {string} casoId
 * @param {string} motivo — obligatorio.
 * @param {string} autor — userId del que rechaza. Debe coincidir con asignadoA.
 */
export async function rechazarCaso(casoId, motivo, autor = null) {
  if (!motivo || !motivo.trim()) {
    throw new Error(`[casos] el motivo es obligatorio para rechazar`)
  }
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)
  if (caso.estado !== CASO_ESTADOS.NUEVO) {
    throw new Error(`[casos] solo se puede rechazar un caso en estado 'nuevo' (actual: ${caso.estado})`)
  }
  if (!caso.asignadoA) {
    throw new Error(`[casos] el caso no tiene asignado — no hay nada que rechazar`)
  }

  const asignadoAnterior = caso.asignadoA

  await updateDoc(casoRef(casoId), forUpdate({
    asignadoA: null,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.CASO_RECHAZADO,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: motivo,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { asignadoAnterior, motivo },
  })

  // ─── Notificar al owner que el caso quedó sin asignar ───
  if (caso.ownerUserId && caso.ownerUserId !== autor) {
    await notificarSeguro({
      userId: caso.ownerUserId,
      tipo: NOTIFICACION_TIPOS.CASO_TRANSFERIDO,
      titulo: `Rechazaron el caso ${caso.numero || casoId.slice(0, 8)}`,
      cuerpo: `${motivo}`,
      link: `/casos/${casoId}`,
      recursoTipo: 'caso',
      recursoId: casoId,
      empresaId: caso.empresaId,
      prioridad: NOTIFICACION_PRIORIDADES.NORMAL,
      metadatos: { tipo: caso.tipo, asignadoAnterior, motivo },
    })
  }
}

/**
 * Escala el caso. Cambia el estado a 'escalado' + asigna al supervisor.
 *
 * @param {string} casoId
 * @param {string} supervisorId
 * @param {string} razon
 * @param {string} autor
 */
export async function escalarCaso(casoId, supervisorId, razon = null, autor = null) {
  // La transición se valida ANTES de transferir: `transferirCaso` ya escribe
  // (asignado, case team, feed y notificación) y no tiene vuelta atrás, así
  // que una transición rechazada después dejaba el caso reasignado sin estar
  // escalado (report rX7S5oKYTeQaR5ysjXZe).
  const casoPrev = await cargarCasoConTransicionValida(casoId, CASO_ESTADOS.ESCALADO)
  // Cambiar asignado al supervisor (suma al case team)
  await transferirCaso(casoId, supervisorId, `Escalado: ${razon || 'sin motivo'}`, autor)
  // Cambiar estado a 'escalado' — por `aplicarTransicion`, no por
  // `cambiarEstado`, que candadea este estado justamente para que nadie
  // llegue acá sin pasar por esta función.
  await aplicarTransicion(casoId, CASO_ESTADOS.ESCALADO, razon, autor)

  // ─── B4.2 — Notificación específica de escalamiento al supervisor ───
  // (transferirCaso ya notificó al supervisor del cambio de asignación, pero
  // la notif de "escalado" tiene mayor urgencia, así que mandamos otra.)
  if (supervisorId && supervisorId !== autor) {
    await notificarSeguro({
      userId: supervisorId,
      tipo: NOTIFICACION_TIPOS.CASO_ESCALADO,
      titulo: `Caso ${casoPrev?.numero || casoId.slice(0, 8)} escalado a vos`,
      cuerpo: razon || casoPrev?.titulo || 'Requiere intervención',
      link: `/casos/${casoId}`,
      recursoTipo: 'caso',
      recursoId: casoId,
      empresaId: casoPrev?.empresaId,
      prioridad: NOTIFICACION_PRIORIDADES.ALTA,
      metadatos: { razon, escaladoPor: autor },
    })
  }
}

/**
 * Cierra un caso con conclusión + motivoCierre.
 *
 * @param {string} casoId
 * @param {string} conclusion
 * @param {string} motivoCierre   — código motivo (resuelto, falso-positivo, duplicado, etc.)
 * @param {string} autor
 * @param {Object} opts           — { estadoFinal, csat }
 */
export async function cerrarCaso(casoId, conclusion, motivoCierre, autor = null, opts = {}) {
  const estadoFinal = opts.estadoFinal || CASO_ESTADOS.CERRADO
  // Validar transición vía `aplicarTransicion`, no vía `cambiarEstado`: los
  // dos `estadoFinal` posibles (`cerrado` y `cerrado-sin-accion`) están
  // candadeados ahí para que nadie llegue a ellos sin pasar por acá.
  await aplicarTransicion(casoId, estadoFinal, conclusion, autor)
  await updateDoc(casoRef(casoId), forUpdate({
    motivoCierre,
    cerradoEn: serverTimestamp(),
    cerradoPor: autor,
    csat: opts.csat || null,
  }))
  await agregarEventoFeed(casoId, await empresaIdDelCaso(casoId), {
    tipo: FEED_TIPOS.CIERRE,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: conclusion,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { motivoCierre, estadoFinal },
  })

  // ─── B4.1 — Resolver todas las slaInstancias activas del caso ───
  // El motor `evaluarSLAsCore` (functions/slas.js) deja de procesarlas. Quedan en historial
  // para el dashboard analytics (B5.1).
  try {
    const instancias = await instanciasVivasDeRecurso('caso', casoId)
    for (const inst of instancias) {
      await resolverSLAInstancia(inst.id)
    }
  } catch (e) {
    console.warn('[cerrarCaso] resolver SLAs falló (no-fatal):', e.message)
  }

  // ─── B4.2 — Notificar al case team del cierre ───
  const caso = await getCaso(casoId)
  if (caso) {
    const destinatarios = caseTeamUserIds(caso, autor)
    if (destinatarios.length > 0) {
      await notificarVariosSeguro(destinatarios, {
        tipo: NOTIFICACION_TIPOS.CASO_CERRADO,
        titulo: `Caso ${caso.numero} cerrado`,
        cuerpo: motivoCierre ? `Motivo: ${motivoCierre}` : 'Caso resuelto',
        link: `/casos/${casoId}`,
        recursoTipo: 'caso',
        recursoId: casoId,
        empresaId: caso.empresaId,
        prioridad: NOTIFICACION_PRIORIDADES.NORMAL,
        metadatos: { motivoCierre, estadoFinal, cerradoPor: autor },
      })
    }
  }
}

/**
 * Reabre un caso cerrado. Incrementa reabiertoCount (métrica de calidad).
 *
 * @param {string} casoId
 * @param {string} motivo
 * @param {string} autor
 */
export async function reabrirCaso(casoId, motivo = null, autor = null) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)

  await aplicarTransicion(casoId, CASO_ESTADOS.REABIERTO, motivo, autor)
  await updateDoc(casoRef(casoId), forUpdate({
    reabiertoCount: (caso.reabiertoCount || 0) + 1,
    cerradoEn: null,
    cerradoPor: null,
    motivoCierre: null,
  }))
  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.REAPERTURA,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: motivo,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
  })

  // ─── B4.2 — Notificar al case team de la reapertura (prioridad alta) ───
  const destinatarios = caseTeamUserIds(caso, autor)
  if (destinatarios.length > 0) {
    await notificarVariosSeguro(destinatarios, {
      tipo: NOTIFICACION_TIPOS.CASO_REABIERTO,
      titulo: `Caso ${caso.numero} reabierto`,
      cuerpo: motivo || 'El caso requiere atención nuevamente',
      link: `/casos/${casoId}`,
      recursoTipo: 'caso',
      recursoId: casoId,
      empresaId: caso.empresaId,
      prioridad: NOTIFICACION_PRIORIDADES.ALTA,
      metadatos: { motivo, reabiertoCount: (caso.reabiertoCount || 0) + 1 },
    })
  }
}

// ──────── Case Team ────────

/**
 * Agrega un participante al case team. Mantiene participantesUserIds[] derivado.
 *
 * @param {string} casoId
 * @param {Object} participante  — { tipo, userId|contacto, rol }
 */
export async function agregarParticipante(casoId, participante) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)

  const part = { ...participante, agregadoEn: new Date().toISOString() }
  const participantes = [...(caso.participantes || []), part]
  const participantesUserIds = calcularParticipantesUserIds(participantes)

  await updateDoc(casoRef(casoId), forUpdate({
    participantes,
    participantesUserIds,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.PARTICIPANTE_AGREGADO,
    autor: 'sistema',
    autorTipo: 'sistema',
    contenido: `Agregado ${part.tipo}: ${part.userId || part.contacto?.email || '(externo)'}`,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: part,
  })
}

/**
 * Quita un participante por userId. Mantiene participantesUserIds[] derivado.
 *
 * @param {string} casoId
 * @param {string} userId
 */
export async function removerParticipante(casoId, userId) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)

  const participantes = (caso.participantes || []).filter(p => !(p.tipo === 'user-interno' && p.userId === userId))
  const participantesUserIds = calcularParticipantesUserIds(participantes)

  await updateDoc(casoRef(casoId), forUpdate({
    participantes,
    participantesUserIds,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.PARTICIPANTE_REMOVIDO,
    autor: 'sistema',
    autorTipo: 'sistema',
    contenido: `Removido user-interno: ${userId}`,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { userId },
  })
}

/**
 * Cambia el owner end-to-end del caso.
 *
 * @param {string} casoId
 * @param {string} nuevoOwnerId
 * @param {string} autor
 */
export async function cambiarOwner(casoId, nuevoOwnerId, autor = null) {
  const caso = await getCaso(casoId)
  if (!caso) throw new Error(`[casos] caso ${casoId} no encontrado`)

  // Sumar al case team si no estaba
  let participantes = caso.participantes || []
  if (nuevoOwnerId && !participantes.find(p => p.userId === nuevoOwnerId && p.tipo === 'user-interno')) {
    participantes = [...participantes, {
      tipo: 'user-interno', userId: nuevoOwnerId, rol: 'owner', agregadoEn: new Date().toISOString(),
    }]
  }
  const participantesUserIds = calcularParticipantesUserIds(participantes)

  await updateDoc(casoRef(casoId), forUpdate({
    owner: nuevoOwnerId,
    participantes,
    participantesUserIds,
    ultimaActividadEn: serverTimestamp(),
  }))

  await agregarEventoFeed(casoId, caso.empresaId, {
    tipo: FEED_TIPOS.CAMBIO_OWNER,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: null,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { ownerAnterior: caso.owner, ownerNuevo: nuevoOwnerId },
  })
}

// ──────── Adjuntos / Aprobaciones ────────

/**
 * Suma un adjunto al caso (archivo, evidencia, captura).
 *
 * @param {string} casoId
 * @param {Object} adjunto  — { nombre, url, tipo, tamano, storageRef, autor }
 * @param {string} autor
 */
export async function agregarAdjunto(casoId, adjunto, autor = null) {
  const empresaId = await empresaIdDelCaso(casoId)
  const payload = {
    nombre: requireString(adjunto.nombre, 'adjunto.nombre'),
    url: adjunto.url || null,
    storageRef: adjunto.storageRef || null,
    tipo: adjunto.tipo || null,
    tamano: Number(adjunto.tamano) || null,
    autor: autor || adjunto.autor || null,
    empresaId,
    creadoEn: serverTimestamp(),
  }
  const ref = await addDoc(adjuntosColRef(casoId), payload)
  await updateDoc(casoRef(casoId), forUpdate({
    ultimaActividadEn: serverTimestamp(),
  }))
  return ref.id
}

/**
 * Solicita aprobación inline en el caso. Crea entry en /aprobaciones + evento al feed.
 *
 * @param {string} casoId
 * @param {string} aprobadorId
 * @param {string} motivo
 * @param {string} autor
 */
export async function solicitarAprobacion(casoId, aprobadorId, motivo = null, autor = null) {
  const empresaId = await empresaIdDelCaso(casoId)
  const payload = {
    aprobadorId,
    motivo,
    solicitadoPor: autor,
    estado: 'pendiente',
    resolucion: null,
    empresaId,
    creadoEn: serverTimestamp(),
  }
  const ref = await addDoc(aprobacionesColRef(casoId), payload)
  await agregarEventoFeed(casoId, empresaId, {
    tipo: FEED_TIPOS.APROBACION_SOLICITADA,
    autor,
    autorTipo: autor ? 'user-interno' : 'sistema',
    contenido: motivo,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { aprobacionId: ref.id, aprobadorId },
  })
  return ref.id
}

// ──────── Vínculos ────────

/**
 * Vincula una tarea hija al caso (bidireccional: requiere que el caller también
 * setee tarea.casoId del lado tareas).
 *
 * @param {string} casoId
 * @param {string} tareaId
 * @param {Object} meta  — opcional, info de la tarea (tipo, titulo) para feed
 */
export async function vincularTareaHija(casoId, tareaId, meta = null) {
  await updateDoc(casoRef(casoId), forUpdate({
    tareasHijas: arrayUnion(tareaId),
    ultimaActividadEn: serverTimestamp(),
  }))
  await agregarEventoFeed(casoId, await empresaIdDelCaso(casoId), {
    tipo: FEED_TIPOS.TAREA_CREADA,
    autor: 'sistema',
    autorTipo: 'sistema',
    contenido: meta?.titulo || null,
    visibilidad: FEED_VISIBILIDAD.INTERNO,
    metadata: { tareaId, ...meta },
  })
}

/**
 * Desvincula una tarea hija (no borra la tarea, solo quita el vínculo).
 */
export async function desvincularTareaHija(casoId, tareaId) {
  await updateDoc(casoRef(casoId), forUpdate({
    tareasHijas: arrayRemove(tareaId),
    ultimaActividadEn: serverTimestamp(),
  }))
}

/**
 * Suma un recurso vinculado (Configuration Item) al caso.
 *
 * @param {string} casoId
 * @param {Object} recurso  — { tipo: 'mlPublicaciones'|'mlOrdenes'|..., id, snapshot? }
 */
export async function vincularRecurso(casoId, recurso) {
  await updateDoc(casoRef(casoId), forUpdate({
    recursosVinculados: arrayUnion({
      tipo: requireString(recurso.tipo, 'recurso.tipo'),
      id: requireString(recurso.id, 'recurso.id'),
      snapshot: recurso.snapshot || null,
      vinculadoEn: new Date().toISOString(),
    }),
    ultimaActividadEn: serverTimestamp(),
  }))
}

// ──────── Exponer en window (admin debug) ────────

if (typeof window !== 'undefined') {
  window.__casos = {
    // CRUD
    crearCaso,
    getCaso,
    actualizarCaso,
    // State machine
    cambiarEstado,
    transferirCaso,
    escalarCaso,
    cerrarCaso,
    reabrirCaso,
    // Case team
    agregarParticipante,
    removerParticipante,
    cambiarOwner,
    // Feed
    agregarComentario,
    agregarEventoFeed,
    // Adjuntos / aprobaciones
    agregarAdjunto,
    solicitarAprobacion,
    // Vínculos
    vincularTareaHija,
    desvincularTareaHija,
    vincularRecurso,
    // Numeración
    generarNumeroCaso,
    // Constantes
    CASO_PRIORIDADES,
    CASO_SEVERIDADES,
    FEED_TIPOS,
    FEED_VISIBILIDAD,
  }
}
